import type { EventContextField } from './eventContextSchemas'
import type { CrmOperation, TriggerMatchConfig } from './resources/types'

export type CrmEventOperation = CrmOperation | '*'

export type ParsedCrmEventInputType = {
  /** CRM model handle from @crm/{model}/...; `*` when the model is a wildcard */
  modelHandle: string
  /** Operation segment: created | updated | deleted | * */
  operation: CrmEventOperation
  /** Canonical bus event type when exact; null for wildcard */
  eventType: `instance.${CrmOperation}` | null
  /** True when the last segment is * */
  isWildcard: boolean
  /** True when the model segment is * (e.g. @crm star /updated) */
  isModelWildcard: boolean
  /** Original @crm/... type string */
  inputType: string
}

export type CrmEventInputOperationOption = {
  value: CrmOperation
  label: string
}

type CrmHandleRef = {
  handle: string
}

export type CrmModelFieldDef = {
  handle: string
  label?: string
  type?: string
  description?: string
}

const CRM_EVENT_INPUT_PREFIX = '@crm/'
const CRM_OPERATIONS: readonly CrmOperation[] = [
  'created',
  'updated',
  'deleted',
]

function isCrmOperation(value: string): value is CrmOperation {
  return (CRM_OPERATIONS as readonly string[]).includes(value)
}

/**
 * Parse workflow input types like @crm/prospect/updated → instance.updated + model.
 */
export function parseCrmEventInputType(
  type: string,
): ParsedCrmEventInputType | null {
  if (!type.startsWith(CRM_EVENT_INPUT_PREFIX)) {
    return null
  }

  const rest = type.slice(CRM_EVENT_INPUT_PREFIX.length)
  const segments = rest.split('/').filter(Boolean)
  if (segments.length !== 2) {
    return null
  }

  const modelHandle = segments[0]!
  const operationSegment = segments[1]!
  const isModelWildcard = modelHandle === '*'

  if (operationSegment === '*') {
    return {
      modelHandle,
      operation: '*',
      eventType: null,
      isWildcard: true,
      isModelWildcard,
      inputType: type,
    }
  }

  if (!isCrmOperation(operationSegment)) {
    return null
  }

  return {
    modelHandle,
    operation: operationSegment,
    eventType: `instance.${operationSegment}`,
    isWildcard: false,
    isModelWildcard,
    inputType: type,
  }
}

/**
 * Whether a workflow @crm/... input type matches a subscription.
 * Requires instance.* bus type and a concrete matchConfig.model.
 * `@crm/{model}/*` accepts created | updated | deleted for that model.
 * Model wildcard + exact op accepts any model for that operation.
 */
export function crmEventInputTypeMatchesSubscription(
  inputType: string,
  subscriptionEventType: string,
  matchConfig?: Pick<TriggerMatchConfig, 'model' | 'operation'> | null,
): boolean {
  const parsed = parseCrmEventInputType(inputType)
  if (!parsed || !subscriptionEventType.startsWith('instance.')) {
    return false
  }

  const op = subscriptionEventType.slice('instance.'.length)
  if (!isCrmOperation(op)) {
    return false
  }

  // Subscriptions are always bound to one workplace model — never `*`.
  if (matchConfig && matchConfig.model == null) {
    return false
  }

  if (
    matchConfig?.model &&
    !parsed.isModelWildcard &&
    matchConfig.model !== parsed.modelHandle
  ) {
    return false
  }

  if (parsed.isWildcard) {
    return true
  }

  return parsed.operation === op
}

/**
 * Derive @crm/{model}/{op} from model handle + operation.
 */
export function deriveCrmWorkflowInputType(
  modelHandle: string,
  operation: CrmEventOperation,
): string {
  return `${CRM_EVENT_INPUT_PREFIX}${modelHandle}/${operation}`
}

/**
 * Build a TriggerMatchConfig seed from a parsed @crm/... input type.
 * For op wildcards, `operation` must be supplied for the specific subscription.
 * For model wildcards, `model` must be the concrete workplace model.
 */
export function buildCrmMatchConfigFromInputType(params: {
  inputType: string
  label?: string
  /** Required when input type is an operation wildcard */
  operation?: CrmOperation
  /** Required when input type is a model wildcard */
  model?: string
  conditions?: TriggerMatchConfig['conditions']
}): TriggerMatchConfig | null {
  const parsed = parseCrmEventInputType(params.inputType)
  if (!parsed) return null

  const operation: CrmOperation | undefined = parsed.isWildcard
    ? params.operation
    : parsed.operation === '*'
      ? undefined
      : parsed.operation

  if (!operation) return null

  const model = parsed.isModelWildcard
    ? params.model?.trim()
    : parsed.modelHandle
  if (!model || model === '*') return null

  const label =
    params.label?.trim() ||
    model.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  return {
    version: 1,
    source: 'crm',
    eventType: `instance.${operation}`,
    label,
    model,
    operation,
    conditions: params.conditions ?? [],
  }
}

/**
 * Filter workplace CRM models by YAML @crm event inputs and attach allowed
 * operation chips. A model wildcard keeps every model and constrains ops;
 * `@crm/prospect/updated` keeps only that model.
 */
export function constrainCrmModelsByEventInputs<T extends CrmHandleRef>(
  models: T[],
  inputs: Array<{ parsed: ParsedCrmEventInputType }>,
): Array<T & { availableOperations: CrmEventInputOperationOption[] }> {
  if (inputs.length === 0) {
    return models.map((model) => ({
      ...model,
      availableOperations: CRM_OPERATIONS.map((op) => ({
        value: op,
        label: op,
      })),
    }))
  }

  const hasModelWildcard = inputs.some((input) => input.parsed.isModelWildcard)
  const exactHandles = new Set(
    inputs
      .filter((input) => !input.parsed.isModelWildcard)
      .map((input) => input.parsed.modelHandle),
  )

  const filtered = hasModelWildcard
    ? models
    : models.filter((model) => exactHandles.has(model.handle))

  return filtered.map((model) => {
    const ops = new Set<CrmOperation>()
    for (const input of inputs) {
      if (
        !input.parsed.isModelWildcard &&
        input.parsed.modelHandle !== model.handle
      ) {
        continue
      }
      if (input.parsed.isWildcard) {
        for (const op of CRM_OPERATIONS) {
          ops.add(op)
        }
      } else if (input.parsed.operation !== '*') {
        ops.add(input.parsed.operation)
      }
    }
    return {
      ...model,
      availableOperations: CRM_OPERATIONS.filter((op) => ops.has(op)).map(
        (op) => ({ value: op, label: op }),
      ),
    }
  })
}

function fieldChildren(
  modelFields: CrmModelFieldDef[],
  parentPath: string,
): EventContextField[] {
  return modelFields.map((field) => ({
    path: `${parentPath}.${field.handle}`,
    label: field.label ?? field.handle.replace(/_/g, ' '),
    type: field.type,
    description: field.description,
  }))
}

/**
 * Build Liquid context fields for CRM instance events, optionally enriched with model fields.
 * `data.record` is the current/last-known snapshot on created, updated, and deleted.
 * Updated (and wildcard) also include before/after/changedFields.
 */
export function buildCrmInstanceContextFields(
  modelFields: CrmModelFieldDef[] | undefined,
  operation: CrmEventOperation | CrmOperation | null | undefined,
): EventContextField[] {
  const fields = modelFields ?? []
  const includeUpdated =
    operation == null || operation === '*' || operation === 'updated'

  const result: EventContextField[] = [
    { path: 'data.model', label: 'Model name', type: 'string' },
    {
      path: 'meta.origin.source',
      label: 'Origin source',
      type: 'string',
      description: 'Where the write came from (ui, workflow, batch_job, …)',
    },
    {
      path: 'meta.origin.appHandle',
      label: 'Origin app',
      type: 'string',
      description:
        'App handle when the write was scoped to an installation (absent for UI edits)',
    },
    {
      path: 'meta.origin.appInstallationId',
      label: 'Origin app installation',
      type: 'string',
    },
    {
      path: 'data.record',
      label: 'Record',
      type: 'object',
      description:
        operation === 'updated'
          ? 'Current record (same as after)'
          : operation === 'deleted'
            ? 'Record snapshot before deletion'
            : operation === '*' || operation == null
              ? 'Current record on all operations (created/deleted snapshot, updated after)'
              : undefined,
      children: fields.length ? fieldChildren(fields, 'data.record') : undefined,
    },
  ]

  if (includeUpdated) {
    result.push(
      {
        path: 'data.before',
        label: 'Before',
        type: 'object',
        description:
          operation === '*' || operation == null
            ? 'Present on updated events'
            : undefined,
        children: fields.length
          ? fieldChildren(fields, 'data.before')
          : undefined,
      },
      {
        path: 'data.after',
        label: 'After',
        type: 'object',
        description:
          operation === '*' || operation == null
            ? 'Present on updated events'
            : undefined,
        children: fields.length
          ? fieldChildren(fields, 'data.after')
          : undefined,
      },
      {
        path: 'data.changedFields',
        label: 'Changed fields',
        type: 'array',
        description: 'Present on updated events',
      },
    )
  }

  return result
}

/**
 * Find workflow inputs that declare @crm/... event payload types.
 */
export function findCrmEventWorkflowInputs(
  inputs: Record<string, { type?: string }>,
): Array<{ name: string; type: string; parsed: ParsedCrmEventInputType }> {
  const result: Array<{
    name: string
    type: string
    parsed: ParsedCrmEventInputType
  }> = []

  for (const [name, schema] of Object.entries(inputs)) {
    const type = schema.type
    if (!type) continue
    const parsed = parseCrmEventInputType(type)
    if (parsed) {
      result.push({ name, type, parsed })
    }
  }

  return result
}

export const DEFAULT_CRM_EVENT_INPUT_MAPPING = '{{ data }}'
